import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useGetPicture } from './useGetPicture'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { UserProfile } from '../../types/user/userProfile'
import { Language } from '../../types/language'
import { ThemeType } from '../../types/contexts/AppContext'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'
import { getMissingProfileFields } from '../../utils/profile/profile'

export function useProfile() {
  const { t } = useTranslation('exceptions')
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [picture, setPicture] = useState<Blob | null>(null)
  const [pictureUrl, setPictureUrl] = useState<string | null>(null)
  const { authInfo, isAuthenticated } = useAuth()
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken)
  const { getPicture } = useGetPicture()

  const normalizeProfile = useCallback(
    (data: UserProfile): UserProfile => ({
      ...data,
      language: data.language ?? Language.RO,
      theme: data.theme
        ? (data.theme.toLowerCase() as ThemeType)
        : ThemeType.LIGHT,
    }),
    []
  )

  const getProfile = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetcher<UserProfile>(`${CONFIG.BOUNCE_API}/api/v1/profile`)
      if (data) {
        setProfile(normalizeProfile(data))
        setError(null)
      }
    } catch (error) {
      console.error(error)
      if (error instanceof ApiError) {
        const translatedMessage = getErrorMessage(error.data)
        setError(translatedMessage)
      } else setError(t('exceptions:unknown'))
    } finally {
      setLoading(false)
    }
  }, [fetcher, normalizeProfile, t])

  const updateLocalProfile = useCallback(
    (updatedProfile: Partial<UserProfile>) => {
      setProfile(currentProfile =>
        currentProfile ? { ...currentProfile, ...updatedProfile } : currentProfile
      )
    },
    []
  )

  const clearProfile = useCallback(() => {
    setProfile(null)
    setPicture(null)
    setLoading(false)
    setError(null)
  }, [])

  useEffect(() => {
    if (isAuthenticated && !profile) getProfile()
  }, [isAuthenticated, profile, getProfile])

  const profileId = profile?.id

  useEffect(() => {
    if (!profileId) {
      return
    }

    let active = true

    void getPicture().then(blob => {
      if (active) {
        setPicture(blob)
      }
    })

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId])

  useEffect(() => {
    if (!picture) {
      setPictureUrl(null)
      return
    }

    const url = URL.createObjectURL(picture)
    setPictureUrl(url)

    return () => URL.revokeObjectURL(url)
  }, [picture])

  const missingProfileFields = profile ? getMissingProfileFields(profile) : []
  const incompleteProfile = missingProfileFields.length > 0

  return {
    profile,
    incompleteProfile,
    missingProfileFields,
    loading,
    error,
    getProfile,
    updateLocalProfile,
    clearProfile,
    pictureUrl,
    setPicture,
  }
}
